import { DocumentReference, DocumentSnapshot } from "@atgof-firebase/types";
import {
  OnNext,
  Unsubscribe,
  flattening,
  onQuerySnapshot,
  onSnapshots,
} from "./onSnapshot";
import { FDXScriptText } from "./formats/fdx";
import _ from "lodash";

export const strokeWidth = 1;
export const eraserWidth = 10;

export type ScriptTextElement = FDXScriptText;
export type ScriptElement = {
  kind?: string | undefined;
  paragraph?: ScriptTextElement[];
  dualCharacter?: number;
  dialogueLineNumber?: number;
};

export type SceneScript = {
  sceneRef: DocumentReference;
  script?: DocumentSnapshot;
  dialogueLineNumberOffset: number;
  yOffset: number | undefined;
};

function latestScriptQuery(sceneRef: DocumentReference, limit?: number) {
  const q = sceneRef.collection("scripts").orderBy("lastImportedAt", "desc");
  return limit === undefined ? q : q.limit(limit);
}

export function onLatestScripts(
  sceneRefs: DocumentReference[],
  onNext: OnNext<SceneScript[] | undefined>,
  prioritiseAnnotatedScripts: boolean = false,
) {
  return onSnapshots(
    sceneRefs,
    (sceneRef) => (onNext) =>
      latestScriptQuery(
        sceneRef,
        prioritiseAnnotatedScripts ? undefined : 1,
      ).onSnapshot(({ empty, docs }) =>
        onNext({
          sceneRef,
          ...(empty
            ? {}
            : {
                script:
                  (prioritiseAnnotatedScripts
                    ? docs.find((doc) => doc.get("annotations"))
                    : undefined) ?? docs[0],
              }),
        }),
      ),
    (
      next:
        | Omit<SceneScript, "dialogueLineNumberOffset" | "yOffset">[]
        | undefined,
    ) => {
      if (!next) {
        onNext(undefined);
        return;
      }
      let next_: SceneScript[] = [];
      let dialogueLineNumberOffset = 0;
      let yOffset: number | undefined = -40;
      for (const sceneScript of next) {
        next_.push({
          ...sceneScript,
          dialogueLineNumberOffset,
          yOffset,
        });
        const script = sceneScript.script;
        dialogueLineNumberOffset += script?.get("dialogueLines") ?? 0;
        const height: number | undefined = script?.get("height");
        yOffset =
          yOffset == null || height == null ? undefined : yOffset + height - 40;
      }
      onNext(next_);
    },
  );
}

export type ScriptReference = {
  sceneRef: DocumentReference;
  scriptPath: string;
};

export function onAllLatestScriptReferences(
  seasonRef: DocumentReference | undefined,
  onNext: OnNext<ScriptReference[] | undefined>,
) {
  return onQuerySnapshot<DocumentSnapshot[]>(
    seasonRef?.collection("episodes"),
    ({ docs }) => docs,
    (docs) =>
      onSnapshots<DocumentReference, ScriptReference[] | undefined>(
        docs?.map(({ ref }) => ref),
        (episodeRef) => (onNext) =>
          onQuerySnapshot<DocumentSnapshot[]>(
            episodeRef.collection("scenes"),
            ({ docs }) => docs,
            (docs) =>
              onSnapshots<DocumentReference, ScriptReference[] | undefined>(
                docs?.map(({ ref }) => ref),
                (sceneRef) => (onNext) =>
                  onQuerySnapshot(
                    latestScriptQuery(sceneRef, 1),
                    ({ docs }) =>
                      docs.flatMap((doc) => [
                        { sceneRef, scriptPath: doc.get("scriptPath") },
                      ]),
                    onNext,
                  ),
                flattening(onNext),
              ),
          ),
        flattening(onNext),
      ),
  );
}

export function onScriptElements(
  scriptPath: string | undefined,
  getScript: (storagePath: string) => Promise<string>,
  onNext: OnNext<ScriptElement[]>,
): Unsubscribe | void {
  if (!scriptPath) {
    onNext([]);
    return;
  }
  let active = true;
  getScript(scriptPath).then((content) => {
    if (!active) return;
    onNext(JSON.parse(content)); // TODO Catch errors
  }); // TODO .catch()
  return () => {
    active = false;
  };
}

export type AnnotationPath = {
  svg: string;
  width?: number;
  color?: string;
  erasing?: boolean;
  version: number;
  os: string;
};

const annotationPathAttrs: (keyof AnnotationPath)[] = [
  "svg",
  "width",
  "color",
  "erasing",
  "version",
  "os",
];

export type AnnotationLayerProps = {
  key: AnnotationLayerKey;
  label: string;
  colour: string;
  paths: AnnotationPath[];
};

export type AnnotationLayerKey = "user" | "pa" | "public";

export function getAnnotationLayer(
  key: AnnotationLayerKey,
  doc: DocumentSnapshot | undefined,
  colour: string,
): AnnotationLayerProps {
  const v = doc?.get("annotations");
  const paths = (v ? JSON.parse(JSON.stringify(v)) : undefined) ?? [];
  return { key, label: "", colour, paths };
}

function annotationEquals(a: AnnotationPath, b: AnnotationPath) {
  return annotationPathAttrs.every((k) => a[k] === b[k]);
}

export function annotationsEqual(
  a: AnnotationPath[] | undefined,
  b: AnnotationPath[] | undefined,
) {
  if (a?.length !== b?.length) return false;
  for (let i = 0; i < (a?.length ?? 0); i++) {
    if (!annotationEquals(a![i], b![i])) return false;
  }
  return true;
}

export function mergeAnnotations(
  a: AnnotationPath[] | undefined,
  b: AnnotationPath[] | undefined,
) {
  return [
    ...(a ?? []),
    ...(b ?? []).filter((p) => !a?.some((p_) => annotationEquals(p, p_))),
  ];
}
